import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import { generateClient } from '@aws-amplify/api';
import { getAIPlatforms } from '@/graphql/queries';
import { upsertAIPlatform } from '@/graphql/mutations';
import useBodyClick from '@/hooks/useBodyClick';
import useArrowKeys from '@/hooks/useArrowKeys';
import useLoadMore from '@/hooks/useLoadMore';
import useSubscription from '@/hooks/useSubscription';
import { dash, downIcon, SpinnerLineDark, upIcon } from '@/utils';
import { getListSort, setListSort } from '@/utils/stickySettings';

const client = generateClient();

export default ({ search }) => { 
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState({});
  const updateBusy = useCallback((id, value) => {
    setBusy(prevState => ({ ...prevState, [id]: value }));
  }, []);

  const { field: defaultSortField, order: defaultSortOrder } = useMemo(() => 
    getListSort({ key: window.location.pathname }) || { field: 'status', order: 'asc' }, []);

  const [sortField, setSortField] = useState(defaultSortField);
  const [sortOrder, setSortOrder] = useState({ [defaultSortField]: defaultSortOrder });

  const { limit, loadMoreButton, offset, rows, setDefaults, setIsLoadingMore, setRows, setTotal } = useLoadMore({ position: 'absolute' });

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const s = (search || '').trim(); 
    return {
      ai_platform: s ? [s] : null, 
      sorting: [
        { 
          field: sortField === 'ai_platform' ? 'status' : sortField, 
          order: sortOrder[sortField] 
        }
      ]
    };
  }, [search, sortField, sortOrder]);

  useSubscription({ filters_ref, limit_ref: limit, offset_ref: offset, rankings_key: 'ai_platforms', setRows });

  const [sidebarItem, _setSidebarItem] = useState();
  const setSidebarItem = useCallback(item => {
    _setSidebarItem(item);
    const queryParams = new URLSearchParams(window.location.search);
    if (!item) { queryParams.delete('ai_platform_id'); } 
    else { queryParams.set('ai_platform_id', item.ai_platform); }
    navigate({ pathname: window.location.pathname, search: `?${queryParams.toString()}` }, { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    const { ai_platform, sorting } = filters_ref.current;
    try {
      const res = await client.graphql({
        query: getAIPlatforms,
        variables: { ai_platform, limit, offset, sorting }
      });
      const payload = res?.data?.getAIPlatforms;
      const data = payload?.ai_platforms ?? [];
      setRows(prevState => (offset === 0 ? data : [...prevState, ...data]));
      setTotal(payload?.total_count ?? data.length);
      setIsLoadingMore(false);
    } catch (error) {
      console.error("GraphQL Error:", error);
      setIsLoadingMore(false);
    }
  }, [filters_ref.current, limit, offset, setRows, setTotal, setIsLoadingMore]);


  useEffect(() => {
    setDefaults();
  }, [search, setDefaults]);

  useEffect(() => { load().catch(console.error); }, [load]);

  useBodyClick({ onClick: setSidebarItem });
  useArrowKeys({ rows, setSidebarRow: setSidebarItem, sidebarRow: sidebarItem });
  
  const handleDelete = useCallback(async (row) => {
    const ok = window.confirm(`Delete AI Platform "${row.ai_platform}"?`);
    if (!ok) return;
    try {
      updateBusy(row.ai_platform, true);
      if (row.ai_platform === sidebarItem?.ai_platform) setSidebarItem();
      await client.graphql({
        query: upsertAIPlatform,
        variables: {
          input: { ai_platforms: { ai_platform: row.ai_platform } },
          input_delete: [{ ai_platform: row.ai_platform, key: "ai_platforms" }],
        },
      });
      setRows((prev) => prev.filter((r) => r.ai_platform !== row.ai_platform));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      alert("Delete failed");
    } finally {
      updateBusy(row.ai_platform, false);
    }
  }, [sidebarItem, setRows, setTotal, updateBusy, setSidebarItem]);

  const handleSort = field => () => {
    const order = (sortField === field && sortOrder[field] === 'asc') ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder({ [field]: order });
    setListSort({ field, key: window.location.pathname, order });
    setDefaults();
  };

  const sortIcon = sortOrder[sortField] === 'asc' ? upIcon : downIcon;
  const stickyThStyle = { position: "sticky", top: 0, backgroundColor: "#F4F4F4", zIndex: 2, fontSize: "0.875rem", textAlign: "left" };
  const platformCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px", minWidth: 200 };
  const lastCellStyle = { padding: "0 12px", width: 220 };

  const table = useMemo(() => (
    <div className="row g-0">
      <div className="col pe-0">
        <div className="border rounded-3 overflow-auto" style={{ maxHeight: `calc(100vh - 250px)` }}>
          <table className="table table-sm table-borderless align-middle mb-0" style={{ width: "100%", fontSize: "0.875rem" }}>
            <thead className="text-muted small" style={{ height: "30px" }}>
              <tr className="border-bottom">
                <th style={stickyThStyle} className="ps-3 cursor-pointer" onClick={handleSort("ai_platform")}>AI Platform {sortField === "ai_platform" && sortIcon}</th>
                <th style={stickyThStyle} className="ps-3 cursor-pointer" onClick={handleSort("status")}>Status {sortField === "status" && sortIcon}</th>
                <th style={stickyThStyle} className="ps-3 cursor-pointer" onClick={handleSort("updated_at")}>Updated At {sortField === "updated_at" && sortIcon}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ai_platform} className={`border-bottom ${sidebarItem?.ai_platform === r.ai_platform ? 'bw-focus-ring' : ''}`} onClick={() => setSidebarItem(r)} style={{ height: "35px" }}>
                  <td className="ps-3 text-truncate" style={platformCellStyle}>
                    <Link to={`/app/customers/agent_settings/ai_platforms/${r.ai_platform}`} className="text-decoration-none fg-purple">
                      <b>{r.ai_platform}</b>
                    </Link>
                    {busy[r.ai_platform] && <SpinnerLineDark />}
                  </td>
                  <td className="ps-3">{r.status || dash}</td>
                  <td className="ps-3" style={lastCellStyle}>
                    <div className="d-flex justify-content-between align-items-center pe-2">
                      <span className="text-muted small">{r.updated_at ? moment(r.updated_at).fromNow() : dash}</span>
                      <div className="d-flex align-items-center">
                        <Link to={`/app/customers/agent_settings/ai_platforms/${r.ai_platform}`} className="me-3" onClick={e => e.stopPropagation()}>
                          <FontAwesomeIcon icon={icons.faPenToSquare} style={{ color: 'gray' }} title="Edit" />
                        </Link>
                        <FontAwesomeIcon icon={icons.faTrashCan} className="cursor-pointer text-danger" onClick={(e) => { e.stopPropagation(); handleDelete(r); }} title="Delete" />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {sidebarItem && (
        <div className="col-auto ps-4 width_300">
          <div className="position-sticky" style={{ top: 0 }}>
            <button className="btn-close float-end" onClick={() => setSidebarItem()}></button>
            <div onClick={event => event.stopPropagation()}>
                <Link to={`/app/customers/agent_settings/ai_platforms/${sidebarItem.ai_platform}`} className="text-decoration-none fg-purple">
                <h5 className="mb-3 fg-purple text-break"><b>{sidebarItem.ai_platform}</b></h5>
                </Link>
                <div className="small text-muted mb-1">Status</div>
                <div className="mb-3 text-break"><b>{sidebarItem.status || dash}</b></div>
                <div className="small text-muted mb-1">Last Updated</div>
                <div className="mb-3">{sidebarItem.updated_at ? moment(sidebarItem.updated_at).format('LLL') : dash}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  ), [rows, sidebarItem, busy, sortField, sortOrder]);

  return (
    <div className="container-fluid px-0">
      {rows?.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
          {table}
          <div className="mt-3">{loadMoreButton}</div>
        </motion.div>
      )}
    </div>
  );
};